import { Icon } from '@iconify-icon/react';
import Link from 'next/link';

import {
  CHATCO_MCP_SCOPE,
  CHATCO_MCP_URL,
  CONNECTOR_HOSTS,
  CONNECTOR_STATUS_CHECKED,
  CONNECTOR_TONE_LABEL,
  type ConnectorHost,
  type ConnectorStatusTone,
} from './connector-hosts';

// Written out in full so Tailwind's content scanner emits every class.
const TONE_CHIP: Record<ConnectorStatusTone, string> = {
  live: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  review: 'bg-amber-50 text-amber-800 ring-amber-200',
  manual: 'bg-sky-50 text-sky-700 ring-sky-200',
};

interface HostLogoProps {
  host: ConnectorHost;
  size?: 'md' | 'lg';
}

/** The vendor's mark in a neutral tile. Decorative: the host name is always printed beside it. */
export const HostLogo: React.FC<HostLogoProps> = ({ host, size = 'md' }) => (
  <span
    aria-hidden="true"
    className={`flex flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white ${
      size === 'lg' ? 'h-14 w-14 text-[28px]' : 'h-10 w-10 text-xl'
    } ${host.logo ? 'text-slate-900' : 'text-slate-400'}`}>
    <Icon
      icon={host.logo ?? host.fallbackIcon ?? 'tabler:apps'}
      style={host.logo && host.logoColor ? { color: host.logoColor } : undefined}
    />
  </span>
);

export const ConnectorToneChip: React.FC<{ tone: ConnectorStatusTone }> = ({ tone }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ring-1 ${TONE_CHIP[tone]}`}>
    {CONNECTOR_TONE_LABEL[tone]}
  </span>
);

/** Availability card that opens every host guide. */
export const ConnectorStatusPanel: React.FC<{ host: ConnectorHost }> = ({ host }) => (
  <div className="not-prose my-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
    <div className="flex items-start gap-4">
      <HostLogo host={host} size="lg" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-base font-semibold text-slate-900">{host.name}</p>
          <ConnectorToneChip tone={host.tone} />
        </div>
        <p className="mt-1 text-sm font-medium text-slate-800">{host.status}</p>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">{host.statusDetail}</p>
      </div>
    </div>
    <dl className="mt-5 grid gap-3 border-t border-slate-100 pt-4 text-sm sm:grid-cols-3">
      <div className="min-w-0">
        <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Server URL
        </dt>
        <dd className="mt-1 break-all font-mono text-[13px] text-slate-900">{CHATCO_MCP_URL}</dd>
      </div>
      <div>
        <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Permission
        </dt>
        <dd className="mt-1 font-mono text-[13px] text-slate-900">{CHATCO_MCP_SCOPE}</dd>
      </div>
      <div>
        <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Status reviewed
        </dt>
        <dd className="mt-1 text-slate-900">{CONNECTOR_STATUS_CHECKED}</dd>
      </div>
    </dl>
  </div>
);

const FLOW_STEPS = [
  {
    icon: 'tabler:apps',
    title: 'Your AI app',
    body: 'Claude, ChatGPT, Cursor, Codex, Grok or another MCP client connects to mcp.chat.co.',
  },
  {
    icon: 'tabler:lock',
    title: 'Chat.co sign-in',
    body: 'You sign in to your own account, choose one workspace and approve the connection to it.',
  },
  {
    icon: 'tabler:users',
    title: 'Authorized experts',
    body: 'Only experts you can already chat with. Access is checked again on every request.',
  },
  {
    icon: 'tabler:quote',
    title: 'Cited answers',
    body: 'Answers arrive with citations. Sources open only with current Chat.co access.',
  },
];

/**
 * Host → consent → experts → cited answers. Built as an ordered list so the
 * sequence is read in order by assistive technology; the arrows are decorative.
 * Stacks vertically on small screens and runs left to right from `md`.
 */
export const ConnectorFlowDiagram: React.FC = () => (
  <figure className="not-prose my-8">
    <ol className="grid list-none gap-6 !p-0 md:grid-cols-4 md:gap-5">
      {FLOW_STEPS.map((step, index) => (
        <li
          key={step.title}
          className="relative rounded-xl border border-primary-100 bg-primary-50/60 p-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white text-base text-primary-700 ring-1 ring-primary-100">
              <Icon icon={step.icon} aria-hidden="true" />
            </span>
            <p className="text-sm font-semibold text-slate-900">
              <span className="sr-only">Step {index + 1}: </span>
              {step.title}
            </p>
          </div>
          <p className="mt-2 text-[13px] leading-relaxed text-slate-600">{step.body}</p>
          {index < FLOW_STEPS.length - 1 && (
            <span
              aria-hidden="true"
              className="absolute -bottom-5 left-1/2 flex -translate-x-1/2 rotate-90 text-lg text-primary-300 md:-right-[1.1rem] md:bottom-auto md:left-auto md:top-1/2 md:-translate-y-1/2 md:translate-x-0 md:rotate-0">
              <Icon icon="tabler:arrow-right" />
            </span>
          )}
        </li>
      ))}
    </ol>
    <figcaption className="mt-3 text-center text-xs text-slate-500">
      How a connection works. The AI app never receives your Chat.co password or an API key.
    </figcaption>
  </figure>
);

/** Cards linking to every host guide, optionally omitting the current page. */
export const ConnectorHostGrid: React.FC<{ exclude?: string }> = ({ exclude }) => (
  <div className="not-prose my-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
    {CONNECTOR_HOSTS.filter((host) => host.slug !== exclude).map((host) => (
      <Link
        key={host.slug}
        href={host.href}
        className="group flex h-full items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 transition hover:border-primary-300 hover:shadow-sm">
        <HostLogo host={host} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-900 group-hover:text-primary-700">
              {host.name}
            </span>
            <ConnectorToneChip tone={host.tone} />
          </span>
          <span className="mt-1 block text-[13px] leading-relaxed text-slate-600">
            {host.status}
          </span>
          <span className="mt-1 block text-xs text-slate-400">{host.surfaces}</span>
        </span>
      </Link>
    ))}
  </div>
);
